/**
 * Error state for submission failures.
 * Includes recovery suggestions for AAA accessibility compliance.
 * Focus management for WCAG 2.4.3 compliance.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

export function ErrorState({ message, onRetry }: ErrorStateProps) {
  const { t } = useTranslation();
  const headingRef = React.useRef<HTMLHeadingElement>(null);

  // Focus on heading when component mounts for screen reader users
  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="w-full py-6 sm:py-12 px-0 sm:px-4">
      <div className="mx-auto max-w-lg">
        <Card className="border-destructive/50 rounded-none sm:rounded-lg border-x-0 sm:border-x">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10" aria-hidden="true">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle ref={headingRef} tabIndex={-1} as="h1" className="text-2xl text-destructive outline-none">
              {t("onboarding.error.title")}
            </CardTitle>
            <CardDescription>{message}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-muted p-4">
              <p className="text-sm text-muted-foreground">
                {t("onboarding.error.description")}
              </p>
            </div>

            {/* Recovery suggestions for AAA compliance */}
            <div className="rounded-lg border border-muted p-4" role="region" aria-labelledby="recovery-title">
              <h2 id="recovery-title" className="text-sm font-medium mb-3">
                {t("onboarding.error.recoverySteps.title")}
              </h2>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  <span>{t("onboarding.error.recoverySteps.checkData")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  <span>{t("onboarding.error.recoverySteps.tryAgain")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  <span>{t("onboarding.error.recoverySteps.newLink")}</span>
                </li>
              </ul>
            </div>

            <Button className="w-full" onClick={onRetry}>
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
              {t("common.tryAgain")}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
